<template>
  <div>

    <Head :title="form.name" />
    <h1 class="mb-8 text-3xl font-bold">
      Transaksi
      <span class="text-indigo-400 font-medium"> / </span>
      <Link class="text-indigo-400 hover:text-indigo-600" href="/porecv">Penerimaan PO</Link>
      <span class="text-indigo-400 font-medium"> / </span>
      {{ purchaseOrderRecv.code }}
      <span class="text-indigo-400 font-medium"> / </span> Edit
    </h1>
    <div class="max-w-3xl bg-white rounded-md shadow overflow-hidden">
      <form @submit="update">
        <div class="flex flex-wrap -mb-8 -mr-6 p-8">
          <text-input :model-value="purchaseOrder.code" class="pb-8 pr-6 w-full md:w-1/3" label="Nomor PO" disabled />
          <text-input :model-value="formatDate(form.recv_date)" class="pb-8 pr-6 w-full md:w-1/3"
            label="Tgl Terima Barang" disabled />
          <text-input :model-value="poRecvStatus" class="pb-8 pr-6 w-full md:w-1/3" label="Status Penerimaan"
            disabled />
          <div class="w-full" />
          <select-input v-model="form.signature_recv01_id" :error="form.errors.signature_recv01_id"
            class="pb-8 pr-6 w-1/2 md:w-1/3" label="Mengetahui" :disabled="!isInputEnabled">
            <option v-for="employee in employees" :key="employee.id" :value="employee.id">{{ employee.name }}</option>
          </select-input>
          <text-input v-model="form.signature_recv02_id" :error="form.errors.signature_recv02_id"
            class="pb-8 pr-6 w-full lg:w-1/3" label="Penerima" :disabled="!isInputEnabled" />
          <textarea-input v-model="form.description" :error="form.errors.description" class="pb-8 pr-6 w-full md:w-1/2"
            label="Description" :disabled="!isInputEnabled" />
          <select-input v-model="form.item_location_id" :error="form.errors.item_location_id"
            class="pb-8 pr-6 w-full md:w-1/2" label="Lokasi Item" :disabled="!isInputEnabled">
            <option v-for="itemLocation in itemLocations" :key="itemLocation.id" :value="itemLocation.id">{{
              itemLocation.name }}</option>
          </select-input>
        </div>

        <div class="-mr-6 px-8 py-4 border-t border-gray-100">
          <h3 class="text-xl" id="attachments">List berkas pendukung: </h3>
          <ul v-for="(docfile, idx) in purchaseOrderRecv.files" :key="idx" class="w-full list-disc">
            <li class="flex gap-2 items-center">
              <span>- </span>
              <a :href="docfile.url" :class="attachmentAccess.download ? 'text-indigo-500 underline' : 'text-red-600'"
                target="_blank" :download="docfile.file_name" :disable="!attachmentAccess.download"> {{
                  docfile.file_name }}</a> ( {{ docfile.size }} )
              <a href="" title="Delete attachment file" @click.prevent="destroyAttachment(docfile.uuid)">
                <Icon icon="ic:baseline-delete-forever" class="w-6 h-6 text-red-600" />
              </a>
            </li>
          </ul>
          <file-input v-model="form.attachment" :error="form.errors.attachment" class="pr-6 py-4 w-full md:w-2/3"
            type="file" accept="image/*, application/pdf" label="Browse dan upload file dokumen pendukung (Max 10MB)"
            @update:model-value="uploadAttachment" v-if="attachmentAccess.upload" />
        </div>

        <div class="flex items-center px-8 py-4 bg-gray-50 border-t border-gray-100">
          <template v-if="isInputEnabled">
            <button class="btn-red ml-auto" type="button" @click.prevent="confirmPenerimaan">
              <span>Confirm</span>
              <span class="hidden md:inline">&nbsp;Penerimaan</span>
            </button>
            <loading-button :loading="form.processing" class="btn-indigo ml-2" type="submit">
              <span>Update</span>
              <span class="hidden md:inline">&nbsp;Penerimaan</span>
            </loading-button>
          </template>
          <template v-else>
            <a :href="`/porecv/${purchaseOrderRecv.id}/print`" as="button" target="_blank" class="btn-green ml-auto">
              Print Penerimaan PO (PDF)
            </a>
          </template>
        </div>
      </form>
    </div>
    <div class="w-full mt-4" />
    <div id="sectionHeader">
      <div id="sectionEx" class="max-w-4xl bg-white rounded-md shadow overflow-hidden py-4 px-6">
        <a class="flex justify-between items-center py-2 overflow-hidden text-ellipsis whitespace-nowrap rounded hover:bg-blue-50 transition duration-300 ease-in-out cursor-pointer mb-3"
          data-mdb-ripple="true" data-mdb-ripple-color="primary" data-bs-toggle="collapse"
          data-bs-target="#collapsesectionEx" aria-expanded="false" aria-controls="collapsesectionEx">
          <span class="text-xl font-bold">Detail Purchase Order No. {{ purchaseOrder.code }}
          </span>
          <Icon icon="fa6-solid:chevron-down" class="ml-1 w-8 h-8 fill-gray-500" />
        </a>
        <ul id="collapsesectionEx" class="relative accordion-collapse collapse" aria-labelledby="sectionEx"
          data-bs-parent="#sectionHeader">
          <div class="flex flex-wrap mb-8">
            <div class="w-full md:w-1/2 p-2">
              <span class="text-sm text-gray-600">Nomor PO:</span>
              <div>
                <Link class="text-indigo-400 hover:text-indigo-600" :href="`/purchaseorders/${purchaseOrder.id}/edit`">
                {{
                  purchaseOrder.code }}</Link>
              </div>
            </div>
            <div class="w-full md:w-1/2 p-2">
              <span class="text-sm text-gray-600">Nomor PR: </span>
              <div>
                <Link class="text-indigo-400 hover:text-indigo-600"
                  :href="`/purchases/${purchaseOrder.purchase_request_id}/edit`">
                {{
                  purchaseOrder.purchase_request }}</Link>
              </div>
            </div>
            <div class="w-full md:w-1/2 p-2">
              <span class="text-sm text-gray-600">Project:</span>
              <div>{{ purchaseOrder.project }}</div>
            </div>
            <div class="w-full md:w-1/2 p-2">
              <span class="text-sm text-gray-600">Kegiatan:</span>
              <div>{{ purchaseOrder.activity }}</div>
            </div>
            <div class="w-full md:w-1/2 p-2">
              <span class="text-sm text-gray-600">Supplier:</span>
              <div>{{ purchaseOrder.organization }}</div>
            </div>
            <div class="w-full md:w-1/2 p-2">
              <span class="text-sm text-gray-600">Status: </span>
              <div>{{ purchaseOrder.status }}</div>
            </div>
            <div class="w-full md:w-1/2 p-2">
              <span class="text-sm text-gray-600">Tanggal: </span>
              <div>{{ formatDate(purchaseOrder.po_date) }}</div>
            </div>
            <div class="w-full md:w-1/2 p-2">
              <span class="text-sm text-gray-600">Operator: </span>
              <div>{{ purchaseOrder.operator }}</div>
            </div>
            <div class="w-full md:w-1/2 p-2">
              <span class="text-sm text-gray-600">Deskripsi: </span>
              <div>{{ purchaseOrder.description }}</div>
            </div>
            <div class="w-full p-2" />
            <div class="w-full md:w-1/2 p-2">
              <span class="text-sm text-gray-600">Total PO: </span>
              <div class="font-bold">{{ formatNumber(purchaseOrder.total, 2) }}</div>
            </div>
            <div class="w-full md:w-1/2 p-2">
              <span class="text-sm text-gray-600">Total Diterima: </span>
              <div class="font-bold">{{ formatNumber(purchaseOrder.total_recv, 2) }}</div>
            </div>
          </div>
          <table class="w-full whitespace-wrap border">
            <tr class="font-bold">
              <td class="w-1/12 text-right py-4 px-4 border-r border-b-2">No.</td>
              <th class="w-3/12 text-left py-4 px-4 border-r border-b-2">Nama Barang</th>
              <th class="w-2/12 text-left py-4 px-4 border-r border-b-2">Satuan</th>
              <th class="w-1/12 text-right py-4 px-4 border-r border-b-2">Jumlah</th>
              <th class="w-2/12 text-right py-4 px-4 border-r border-b-2">Harga</th>
              <th class="w-2/12 text-right py-4 px-4 border-r border-b-2">Total Harga</th>
              <th class="w-1/12 text-right py-4 px-4 border-r border-b-2">Qty Diterima</th>
            </tr>
            <tr v-for="(row, rowIndex) in purchaseOrderDetails" :key="rowIndex">
              <td class="text-right py-2 px-4 border-r">{{ ++rowIndex }}.</td>
              <td class="text-left py-2 px-2 border-r">{{ row.item }}</td>
              <td class="text-left py-2 px-2 border-r">{{ row.item_unit }}</td>
              <td class="text-right py-2 px-2 border-r">{{ formatNumber(row.qty) }}</td>
              <td class="text-right py-2 px-2 border-r">{{ formatNumber(row.price, 2) }}</td>
              <td class="text-right py-2 px-2 border-r">{{ formatNumber(row.qty * row.price, 2) }}</td>
              <td class="text-right py-2 px-2 border-r">{{ formatNumber(row.qty_recv) }}</td>
            </tr>
          </table>
        </ul>
      </div>
    </div>
    <div class="w-full mt-4" />
    <div class="mt-6 bg-white rounded shadow overflow-x-auto w-full max-w-4xl">
      <table class="w-full whitespace-nowrap">
        <tr class="font-bold">
          <td class="text-center align-middle border-r px-2">No.</td>
          <th class="pb-4 pt-6 px-6 w-2/6 text-left">Nama Barang</th>
          <th class="pb-4 pt-6 px-6 w-1/6 text-left">Satuan</th>
          <th class="pb-4 pt-6 px-6 w-1/6 text-right">Diterima</th>
          <th class="pb-4 pt-6 px-6 w-2/6 text-left">Description</th>
        </tr>
        <detail v-for="(orderDetail, idx) in purchaseOrderRecvDetails.data" :key="orderDetail.id"
          :editable-mode="orderDetail.editable" :order-detail="orderDetail" :items="items" :errors="$page.props.errors"
          :row-index="++idx" :disabled="!purchaseOrderRecv.editable"
          :current-page="purchaseOrderRecvDetails.current_page" :per-page="purchaseOrderRecvDetails.per_page"
          @destroy:detail="destroyDetail" @edit:detail="editDetail" @cancel="cancel('edit')" @save="updateDetail" />
        <tr v-if="purchaseOrderRecvDetails.data.length === 0">
          <td class="px-6 py-4 border-t" colspan="7">No details found.</td>
        </tr>
      </table>
    </div>
    <pagination class="mt-6" :links="purchaseOrderRecvDetails.links" />
  </div>
</template>

<script lang="js">
import Layout from '@/Shared/Layout'
import { Head, Link, router } from '@inertiajs/vue3'
import { format, parse } from 'date-fns'
import { Icon } from '@iconify/vue'
import TextInput from '@/Shared/TextInput'
import TextareaInput from '@/Shared/TextareaInput'
import LoadingButton from '@/Shared/LoadingButton'
import SelectInput from '@/Shared/SelectInput'
import Detail from '@/Pages/PurchaseOrdersRecv/DetailRow'
import Pagination from '@/Shared/Pagination'
import find from 'lodash/find'
import FileInput from '@/Shared/FileInput'

export default {
  components: {
    Head,
    Link,
    Icon,
    LoadingButton,
    SelectInput,
    TextInput,
    TextareaInput,
    Detail,
    Pagination,
    FileInput,
  },
  layout: Layout,
  props: {
    purchaseOrder: Object,
    purchaseOrderDetails: Array,
    purchaseOrderRecv: Object,
    purchaseOrderRecvDetails: Object,
    employees: Object,
    items: Object,
    itemLocations: Object,
    attachmentAccess: Object
  },
  remember: 'form',
  data() {
    return {
      form: this.$inertia.form({
        purchase_order_m_id: this.purchaseOrderRecv.purchase_order_m_id,
        recv_date: this.purchaseOrderRecv.recv_date,
        signature_recv01_id: this.purchaseOrderRecv.signature_recv01_id,
        signature_recv02_id: this.purchaseOrderRecv.signature_recv02_id,
        signature_recv03_id: this.purchaseOrderRecv.signature_recv03_id,
        description: this.purchaseOrderRecv.description,
        item_location_id: this.purchaseOrderRecv.item_location_id,
        editable: this.purchaseOrderRecv.editable,
        attachment: null,
      }),
      editedItem: {
        id: null,
        item_id: null,
        qty_recv: 0,
        editable: false,
        desc: null,
      },
      detailKeyRefresh: 0,
    }
  },
  computed: {
    poRecvStatus() {
      return this.purchaseOrderRecv.editable ? 'DRAFT' : 'LOCKED'
    },
    isInputEnabled() {
      return this.purchaseOrderRecv.editable
    },
  },
  methods: {
    update() {
      this.form.post(`/porecv/${this.purchaseOrderRecv.id}`, {
        preserveScroll: true,
        onSuccess: () => this.form.reset('attachment'),
      })
    },
    formatNumber(num, precision = 0) {
      return num ? num.toLocaleString('id-ID', { minimumFractionDigits: precision }) : '0'
    },
    formatDate(ymd) {
      return format(parse(ymd, 'yyyy-MM-dd', new Date()), 'dd-MMM-yyyy')
    },
    uploadAttachment() {
      this.form.attachment && this.update()
    },
    destroyAttachment(uuid) {
      if (confirm('Are you sure you want to delete this attachment file?')) {
        router.delete(`/attachment/${uuid}`, {
          preserveScroll: true
        })
      }
    },
    editDetail(detail) {
      let edited = find(this.purchaseOrderRecvDetails.data, ['id', detail.id])
      edited.editable = true
    },
    destroyDetail(detailId) {
      router.delete(`/porecvdetails/${detailId}`, {
        preserveScroll: true,
        onBefore: () => confirm('Anda yakin ingin menghapus item ini dari detail penerimaan barang PO?')
      })
    },
    resetEditedItem() {
      this.editedItem = {
        id: null,
        item_id: null,
        qty_recv: 0,
        editable: false,
        desc: null,
      }
    },
    cancel(action) {
      if (action === 'create')
        this.resetEditedItem()
      if (action === 'edit')
        this.$inertia.reload({ only: ['purchaseOrderRecvDetails'] })
    },
    updateDetail(value) {
      const formDetail = this.$inertia.form({
        item_id: value.item_id,
        qty_recv: value.qty_recv,
        item_unit_id: value.item_unit_id,
        desc: value.desc,
      })
      formDetail.put(`/porecvdetails/${value.id}`, {
        preserveScroll: true,
        onError: () => {
          this.editedItem = value
        },
      })
    },
    confirmPenerimaan() {
      if (confirm('Informasi penerimaan dan detailnya tidak bisa diubah lagi setelah proses ini, konfirmasikan?')) {
        this.form.editable = false
        this.update()
      }
    },
  },
}
</script>
